/**
 * MobileReasonDrawer — bottom sheet that slides up to prompt the user for a
 * reason code before finishing an alert.
 *
 * Anchors to the nearest positioned ancestor — the consuming page wraps it in
 * a `position: relative` container so the backdrop covers only that area.
 *
 * Mirrors palcare_mobile_android ReasonsBottomSheet.kt:
 *   - Top strip: Close (X) left, title center, Save right
 *   - Segmented tabs (one per distinct reason_type_name / reason_code_type_id)
 *   - Radio list of reasons filtered to the active tab
 *
 * Reason code records look like:
 *   { cloud_reason_code_id, reason_code_description, pal_reason_description,
 *     reason_code_type_id, reason_type_name }
 * Type id/name may be null for legacy rows; those fall into an "Other" tab.
 */

import { useEffect, useMemo, useState } from 'react';
import '../../styles/mobile-alerts.css';

const OTHER_KEY = '__other__';

const MobileReasonDrawer = ({
    open,
    row,
    reasonCodes,
    reasonCodeTypes,
    saving,
    onSubmit,
    onCancel,
}) => {
    /** reason_code_type_id → reason_type_name lookup (the join CloudReasonSettingsTab does). */
    const typeLookup = useMemo(() => {
        const map = {};
        (reasonCodeTypes || []).forEach((t) => {
            map[t.reason_code_type_id] = t.reason_type_name;
        });
        return map;
    }, [reasonCodeTypes]);

    /** Build tabs from distinct reason_code_type_id values, resolving names via lookup. */
    const tabs = useMemo(() => {
        const map = new Map();
        (reasonCodes || []).forEach((rc) => {
            const id = rc.reason_code_type_id ?? OTHER_KEY;
            const name =
                rc.reason_type_name ||
                typeLookup[rc.reason_code_type_id] ||
                'Other';
            if (!map.has(id)) map.set(id, { id, name });
        });
        return [...map.values()].sort((a, b) => {
            if (a.id === OTHER_KEY) return 1;
            if (b.id === OTHER_KEY) return -1;
            return a.name.localeCompare(b.name);
        });
    }, [reasonCodes, typeLookup]);

    const [activeTab, setActiveTab] = useState(() => tabs[0]?.id ?? null);
    const [reasonId, setReasonId] = useState('');

    // Reset when drawer opens for a new row
    useEffect(() => {
        if (open) {
            setActiveTab(tabs[0]?.id ?? null);
            setReasonId('');
        }
    }, [open, row?.event_id, tabs]);

    // Close on Escape
    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e) => {
            if (e.key === 'Escape' && !saving) onCancel?.();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, saving, onCancel]);

    const filtered = useMemo(() => {
        if (activeTab == null) return [];
        return (reasonCodes || [])
            .filter((rc) => (rc.reason_code_type_id ?? OTHER_KEY) === activeTab)
            .sort((a, b) => (a.reason_code_description || '').localeCompare(b.reason_code_description || ''));
    }, [reasonCodes, activeTab]);

    if (!open) return null;

    const handleSave = () => {
        const id = parseInt(reasonId, 10);
        if (!id || saving) return;
        onSubmit?.(id);
    };

    return (
        <div className="mrd-backdrop" onClick={() => { if (!saving) onCancel?.(); }}>
            <div className="mrd-sheet" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Select a reason">
                <div className="mrd-header">
                    <button
                        type="button"
                        className="mrd-icon-btn"
                        onClick={onCancel}
                        disabled={saving}
                        aria-label="Close"
                    >
                        {'\u2715'}
                    </button>
                    <div className="mrd-title">Select a reason</div>
                    <button
                        type="button"
                        className="mrd-save-btn"
                        onClick={handleSave}
                        disabled={saving || !reasonId}
                    >
                        {saving ? 'Saving…' : 'Save'}
                    </button>
                </div>

                {row ? (
                    <div className="mrd-subtitle">
                        {row.area_name || row.resident_name || row.event_type || 'Alert'}
                    </div>
                ) : null}

                {tabs.length > 0 ? (
                    <div className="mrd-tabs" role="tablist">
                        {tabs.map((t) => (
                            <button
                                key={t.id}
                                type="button"
                                role="tab"
                                aria-selected={t.id === activeTab}
                                className={`mrd-tab${t.id === activeTab ? ' mrd-tab--active' : ''}`}
                                onClick={() => { setActiveTab(t.id); setReasonId(''); }}
                                disabled={saving}
                            >
                                {t.name}
                            </button>
                        ))}
                    </div>
                ) : null}

                <div className="mrd-list">
                    {filtered.length === 0 ? (
                        <div className="mrd-empty">No reasons configured for this type.</div>
                    ) : filtered.map((rc) => {
                        const id = rc.cloud_reason_code_id;
                        const idStr = String(id);
                        const checked = reasonId === idStr;
                        return (
                            <label key={id} className={`mrd-row${checked ? ' mrd-row--checked' : ''}`}>
                                <input
                                    type="radio"
                                    name="mrd-reason"
                                    className="mrd-radio"
                                    checked={checked}
                                    disabled={saving}
                                    onChange={() => setReasonId(idStr)}
                                />
                                <span className="mrd-row__text">{rc.reason_code_description}</span>
                            </label>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default MobileReasonDrawer;
